import { IdentityProviders, useSignInWithRedirect } from 'korint-sdk/react';
import { useTranslation } from 'react-i18next';

import { IconApple, IconFacebook, IconGoogle } from '../../icons';
import { Button } from '../../ui';
import { saveLastLoginMethod } from '../../../utils/last-login-method';
import { saveOAuthRedirect } from '../../../utils/oauth-redirect';
import { Center } from '@folio/components/ui/flex';

export const IdentityProvider = ({
  providers = [],
  context,
  lastUsedProvider,
  enableLastLoginTracking = false,
}: {
  providers: IdentityProviders[];
  context: 'signin' | 'signup';
  lastUsedProvider?: string | null;
  enableLastLoginTracking?: boolean;
}) => {
  const { t } = useTranslation();
  const { signInWithRedirect, isLoading: redirectLoading } = useSignInWithRedirect();

  const handleProviderClick = (provider: IdentityProviders) => {
    if (enableLastLoginTracking) {
      saveLastLoginMethod(provider);
    }

    saveOAuthRedirect(window.location.search);

    // Clear query parameters from URL before OAuth flow to prevent them from being preserved
    // Use window.history.replaceState for immediate, synchronous URL update
    if (window.location.search) {
      const urlWithoutQuery = window.location.pathname;
      window.history.replaceState(null, '', urlWithoutQuery);
    }

    signInWithRedirect({
      provider: provider,
      options: {
        preferPrivateSession: true,
      },
    });
  };

  return (
    <>
      {providers.map(provider => {
        const isLastUsed = lastUsedProvider === provider;
        return (
          <Button
            key={provider}
            variant={isLastUsed ? 'default' : 'outline'}
            className={`w-full mb-4 ${isLastUsed ? 'ring-2 ring-primary ring-offset-2' : ''}`}
            loading={redirectLoading}
            leftIcon={
              provider === IdentityProviders.GOOGLE ? (
                <IconGoogle className="h-6 w-6 mr-1" />
              ) : provider === IdentityProviders.APPLE ? (
                <IconApple className="h-6 w-6 mr-1" />
              ) : provider === IdentityProviders.FACEBOOK ? (
                <IconFacebook className="h-6 w-6 mr-1" />
              ) : null
            }
            onClick={() => handleProviderClick(provider)}
          >
            {context === 'signin'
              ? t('authSignIn.continueWithProvider', { provider })
              : t('authSignIn.signUpWithProvider', { provider })}
          </Button>
        );
      })}
      {providers.length > 0 && <Center className="my-4">{t('authSignIn.or')}</Center>}
    </>
  );
};
